import { SaveAnalysisForm } from '@modules/analysis/components/SaveAnalysisForm';
import { useSaveAnalysis } from '@modules/analysis/hooks/useSaveAnalysis';
import { AnalysisFeedbackListDrawerHeader } from '@modules/common/components/AnalysisFeedbackListDrawerHeader';
import { AnalysisFeedbackListDrawerSecondaryNode } from '@modules/common/components/AnalysisFeedbackListDrawerSecondaryNode';
import { CircularLoader } from '@modules/common/components/CircularLoader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { SaveArtefactConfirmDialog } from '@modules/common/components/SaveArtefact/SaveArtefactConfirmDialog';
import { useCopyToClipboard } from '@modules/common/hooks';
import { getTenantSlugFromURL } from '@modules/common/utils';
import { trackWisdomEvents } from '@modules/oracle/utils';
import {
  useGetQuantifyChartEQLProps,
  useGetQuantifyCompareQuery,
  useGetQuantifyQueryMetadata,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
} from '@mui/material';
import { AnalysisUseCase, AnalysisViewFragment } from 'generated/graphql';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Scope } from 'v2/core/entities/scope';
import { getAnalysisLink } from 'v2/core/services/analysis/link';
import { OracleQuantifyStepDefinition } from '../OracleQuantifyStepDefinition';

interface QuantifyCitationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isReadOnly?: boolean;
  analysis: AnalysisViewFragment | undefined;
}

export const QuantifyCitationDialog = ({
  isOpen,
  onClose,
  isReadOnly,
  analysis,
}: QuantifyCitationDialogProps) => {
  const copyToClipboard = useCopyToClipboard();
  const metadata = useGetQuantifyQueryMetadata();
  const eqlProps = useGetQuantifyChartEQLProps();
  const compareQuery = useGetQuantifyCompareQuery();
  const useCase = compareQuery
    ? AnalysisUseCase.Compare
    : AnalysisUseCase.Default;

  const [savedAnalysisRoute, setSavedAnalysisRoute] = useState<null | string>(
    null,
  );
  const [isSaveAnalysisFormOpen, setIsSaveAnalysisFormOpen] = useState(false);

  const groupByLabel = useQuantifyStore(
    ({ query }) => query.groupByField?.displayName,
  );
  const shouldShowAnomalies = useQuantifyStore(
    ({ query }) => query.shouldShowAnomalies,
  );

  const openSaveAnalysisForm = () => {
    setIsSaveAnalysisFormOpen(true);

    trackWisdomEvents({
      eventName:
        'Wisdom - Clicked on Save As New Artefact from Feedback Citation View',
      artefact: {
        id: analysis?.id,
        title: analysis?.title,
        useCase: analysis?.useCase,
      },
    });
  };

  const { showSuccessNotification } = useNotificationContext();

  const { onSaveAnalysis } = useSaveAnalysis({
    eqlProps: eqlProps,
    metadata: metadata,
    shouldRedirect: false,
    useCase: useCase,
    eventProps: {
      quantifyQueryOptions: {
        showMeLabel: groupByLabel,
        shouldShowAnomalies,
      },
    },
  });

  const closeAnalysisForm = () => setIsSaveAnalysisFormOpen(false);

  const onSubmitAnalysisForm = async (name: string, scope: Scope) => {
    const analysisDetails = await onSaveAnalysis({ name, scope });
    setSavedAnalysisRoute(analysisDetails.artefactRoute);

    showSuccessNotification({
      contentComponent: 'Quantify Saved Successfully',
      props: {
        isClosable: true,
        action: (
          <Button
            component={Link}
            to={`${analysisDetails.artefactRoute}`}
            target="_blank"
            variant="text"
            color="success"
            size="small"
          >
            Open
          </Button>
        ),
      },
    });

    return analysisDetails;
  };

  const onCopyLink = () => {
    if (!analysis) {
      return;
    }

    const link = getAnalysisLink({
      useCase: analysis.useCase,
      name: analysis.title,
      token: analysis.viewToken,
      tenantSlug: getTenantSlugFromURL(),
    });

    copyToClipboard(link, 'Quantify Link copied link to Clipboard');

    trackWisdomEvents({
      eventName: 'Wisdom - Copied Artefact Link from Feedback Citation View',
      artefact: {
        id: analysis.id,
        title: analysis.title,
        useCase: analysis.useCase,
        link,
      },
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ elevation: 0 }}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer,
        '& .MuiDialogContent-root': {
          padding: 2,
        },
        '& .MuiDialogActions-root': {
          padding: 1,
        },
      }}
    >
      {!analysis ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          width="100%"
          py={10}
        >
          <CircularLoader size={50} />
        </Stack>
      ) : (
        <>
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            p={2}
          >
            <AnalysisFeedbackListDrawerHeader
              useCase={AnalysisUseCase.Default}
              title={analysis.title}
              createdBy={`${analysis.createdBy.firstName} ${analysis.createdBy.lastName}`}
            />
            <AnalysisFeedbackListDrawerSecondaryNode
              onCopy={onCopyLink}
              useCase={AnalysisUseCase.Default}
              onCreateNewArtefact={openSaveAnalysisForm}
            />
            <SaveArtefactConfirmDialog
              artefactRoute={savedAnalysisRoute}
              fullWidth
              maxWidth="sm"
              shouldSkipRedirect
              PaperProps={{ elevation: 3 }}
              open={isSaveAnalysisFormOpen}
              onClose={closeAnalysisForm}
            >
              <SaveAnalysisForm
                headerText="Save new Quantify"
                buttonText="Save as new"
                onSubmit={onSubmitAnalysisForm}
                onClose={closeAnalysisForm}
              />
            </SaveArtefactConfirmDialog>
          </Stack>
          <Divider
            sx={{
              opacity: 0.12,
            }}
          />
          <DialogContent dividers>
            <OracleQuantifyStepDefinition
              isReadOnly={isReadOnly}
              eqlQuery={analysis.context.eql}
            />
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
